import React from "react";
import { Text, Flex } from "@jsq/ds/core";
import { DetailedWirePaymentPrefObjectType } from "../../../common/types";
import FormattedAddress from "../FormattedAddress";
import PaymentDetailsModalField from "../PaymentDetailsModalField";
import { getAddress } from "../utils";
import { WireInfoField } from "./WireInfoField";

export type WirePaymentPrefDisplayProps = {
  wirePaymentPrefFull: DetailedWirePaymentPrefObjectType;
};

export const WirePaymentPrefDisplay: React.FC<WirePaymentPrefDisplayProps> = ({
  wirePaymentPrefFull,
}: WirePaymentPrefDisplayProps) => {
  const obiInfo = WireInfoField({ wireInfo: wirePaymentPrefFull.wireObi });
  const bbiInfo = WireInfoField({ wireInfo: wirePaymentPrefFull.wireBbi });
  const beneficiaryAddress =
    wirePaymentPrefFull.beneficiaryAddress &&
    getAddress(wirePaymentPrefFull.beneficiaryAddress);
  const bankAddress =
    wirePaymentPrefFull.bank?.address && getAddress(wirePaymentPrefFull.bank.address);
  const intermediaryBankAddress =
    wirePaymentPrefFull.intermediaryBank?.address &&
    getAddress(wirePaymentPrefFull.intermediaryBank.address);

  const gap = 8; // gap between rows and columns

  return (
    <Flex gap={gap} flexDirection="column" data-testid="WirePaymentPrefDisplayFlex">
      <PaymentDetailsModalField
        label="Account holder name"
        value={wirePaymentPrefFull.beneficiaryName}
        gap={gap}
      />
      <PaymentDetailsModalField
        label="Bank name"
        value={wirePaymentPrefFull.bank?.name}
        gap={gap}
      />
      {wirePaymentPrefFull.bank?.abaNumber && (
        <PaymentDetailsModalField
          label="ABA routing number"
          value={wirePaymentPrefFull.bank.abaNumber}
          gap={gap}
        />
      )}
      {wirePaymentPrefFull.bank?.swiftCode && (
        <PaymentDetailsModalField
          label="SWIFT / BIC code"
          value={wirePaymentPrefFull.bank.swiftCode}
          gap={gap}
        />
      )}
      {wirePaymentPrefFull.bank?.intlRoutingCode && (
        <PaymentDetailsModalField
          label="International routing code"
          value={wirePaymentPrefFull.bank.intlRoutingCode}
          gap={gap}
        />
      )}
      <PaymentDetailsModalField
        label="Account number"
        value={wirePaymentPrefFull.beneficiaryAccountNumber}
        gap={gap}
      />
      {obiInfo && (
        <Flex gap={gap}>
          <Flex flexBasis="60%">
            <Text variant="secondary-md">OBI</Text>
          </Flex>
          <Flex flexBasis="100%" flexDirection="column">
            {obiInfo}
          </Flex>
        </Flex>
      )}
      {wirePaymentPrefFull.referenceToBeneficiary && (
        <PaymentDetailsModalField
          label="Reference to beneficiary"
          value={wirePaymentPrefFull.referenceToBeneficiary}
          gap={gap}
        />
      )}
      {bbiInfo && (
        <Flex gap={gap}>
          <Flex flexBasis="60%">
            <Text variant="secondary-md">BBI</Text>
          </Flex>
          <Flex flexBasis="100%" flexDirection="column">
            {bbiInfo}
          </Flex>
        </Flex>
      )}
      <Flex gap={gap}>
        <Flex flexBasis="60%">
          <Text variant="secondary-md">Account holder address</Text>
        </Flex>
        <Flex flexBasis="100%" flexDirection="column">
          {beneficiaryAddress && (
            <FormattedAddress address={beneficiaryAddress} variant="regular-md" />
          )}
        </Flex>
      </Flex>
      <Flex gap={gap}>
        <Flex flexBasis="60%">
          <Text variant="secondary-md">Bank address</Text>
        </Flex>
        <Flex flexBasis="100%" flexDirection="column">
          {bankAddress && <FormattedAddress address={bankAddress} variant="regular-md" />}
        </Flex>
      </Flex>
      {wirePaymentPrefFull.intermediaryBank && (
        <>
          <PaymentDetailsModalField
            label="Intermediary bank"
            value={wirePaymentPrefFull.intermediaryBank.name}
            gap={gap}
          />
          {wirePaymentPrefFull.intermediaryBank.abaNumber && (
            <PaymentDetailsModalField
              label="Intermediary bank ABA routing number"
              value={wirePaymentPrefFull.intermediaryBank.abaNumber}
              gap={gap}
            />
          )}
          {wirePaymentPrefFull.intermediaryBank.swiftCode && (
            <PaymentDetailsModalField
              label="Intermediary bank SWIFT / BIC code"
              value={wirePaymentPrefFull.intermediaryBank.swiftCode}
              gap={gap}
            />
          )}
          {wirePaymentPrefFull.intermediaryBank.intlRoutingCode && (
            <PaymentDetailsModalField
              label="Intermediary bank international routing code"
              value={wirePaymentPrefFull.intermediaryBank.intlRoutingCode}
              gap={gap}
            />
          )}
          <Flex gap={gap}>
            <Flex flexBasis="60%">
              <Text variant="secondary-md">Intermediary bank address</Text>
            </Flex>
            <Flex flexBasis="100%" flexDirection="column">
              {intermediaryBankAddress && (
                <FormattedAddress address={intermediaryBankAddress} variant="regular-md" />
              )}
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};
